import { ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'

export function useAuthPersistence() {
  const authStore = useAuthStore()
  const isRestoring = ref(false)

  // Função para detectar atividade do usuário
  const setupUserActivityDetection = () => {
    let lastActivity = Date.now()

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    const updateActivity = () => {
      lastActivity = Date.now()
    }

    // Adiciona listeners para eventos de atividade
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true)
    })

    // Verifica periodicamente se o usuário está ativo
    setInterval(
      async () => {
        const now = Date.now()
        const inactiveTime = now - lastActivity

        // Se o usuário esteve ativo nos últimos 5 minutos e está autenticado
        if (inactiveTime < 5 * 60 * 1000 && authStore.isAuthenticated) {
          try {
            // Verifica se o token precisa ser renovado (renova se faltam menos de 10 minutos)
            const user = authStore.user
            if (user) {
              await authStore.refreshToken()
            }
          } catch (error) {
            console.error('Erro ao verificar/renovar token:', error)
          }
        }
      },
      5 * 60 * 1000
    ) // Verifica a cada 5 minutos

    return () => {
      // Cleanup function
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true)
      })
    }
  }

  // Função para restaurar sessão do localStorage se necessário
  const restoreSession = async () => {
    isRestoring.value = true

    try {
      // O Firebase já cuida da persistência automaticamente
      // Apenas aguardamos a inicialização
      await new Promise<void>(resolve => {
        const unwatch = watch(
          () => authStore.isInitialized,
          initialized => {
            if (initialized) {
              unwatch()
              resolve()
            }
          },
          { immediate: true }
        )
      })
    } catch (error) {
      console.error('Erro ao restaurar sessão:', error)
    } finally {
      isRestoring.value = false
    }
  }

  // Função para lidar com mudanças de visibilidade da página
  const handleVisibilityChange = async () => {
    if (!document.hidden && authStore.isAuthenticated) {
      // Página ficou visível novamente, verifica/renova token
      try {
        await authStore.refreshToken()
      } catch (error) {
        console.error('Erro ao renovar token ao voltar para a página:', error)
      }
    }
  }

  // Setup dos event listeners
  const setup = () => {
    // Detecta atividade do usuário para refresh automático
    const cleanupActivity = setupUserActivityDetection()

    // Listener para mudanças de visibilidade da página
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup function
    return () => {
      cleanupActivity()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }

  return {
    isRestoring,
    restoreSession,
    setup
  }
}
